import { supabase } from "./supabase";
import type { Escrita } from "./conteudo";
import {
  CHAVES_CURSO,
  CHAVES_EXPERIENCIA,
  CHAVES_FORMACAO,
  CHAVES_IDIOMA,
  paraGravar,
  temaValido,
  type Curriculo,
  type Tema,
} from "./curriculoModelo";

// O lado de banco do Gerador de Currículos (000100/000101).
//
// A leitura é direta na tabela, com a RLS de dono fazendo o filtro — igual a
// `perfis_profissionais`. A ESCRITA é toda por RPC: `authenticated` não tem
// insert, update nem delete em `curriculos`, e é por isso que este arquivo não
// tem um único `.update()`.
//
// A diferença com o app de origem está aqui, em duas linhas de código que não
// existem: lá o `useResumes.ts` fazia `.update().eq('user_email', email)` — o
// filtro por dono era um argumento que o cliente escolhia. Com a policy
// `FOR ALL TO anon USING (true)`, trocar aquele argumento dava acesso ao
// currículo de outra pessoa. Aqui o dono não é argumento: é `meu_profile_id()`
// dentro da função.

function falha(mensagem: string | undefined): { ok: false; mensagem: string } {
  return {
    ok: false,
    mensagem: mensagem?.trim() ? mensagem : "Não foi possível concluir agora. Tente de novo em instantes.",
  };
}

const COLUNAS =
  "id, titulo, tema, nome, email, telefone, linkedin, localidade, resumo, " +
  "habilidades, informacoes_adicionais, experiencias, formacoes, cursos, idiomas, atualizado_em";

/**
 * Normaliza o que vem do banco.
 *
 * `tema` é conferido mesmo tendo constraint: a coluna aceita só os cinco, mas se
 * um dia um sexto entrar por migration e a tela ainda não tiver o componente, é
 * melhor cair no clássico aqui do que renderizar vazio. O app de origem fazia
 * isso no `default` de um switch, sem dizer — a diferença é que aqui está dito.
 */
function daLinha(l: Record<string, unknown>): Curriculo {
  const lista = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const texto = (v: unknown) => (typeof v === "string" ? v : "");
  const tema = texto(l.tema);
  return {
    id: String(l.id),
    titulo: texto(l.titulo),
    tema: (temaValido(tema) ? tema : "classico") as Tema,
    nome: texto(l.nome),
    email: texto(l.email),
    telefone: texto(l.telefone),
    linkedin: texto(l.linkedin),
    localidade: texto(l.localidade),
    resumo: texto(l.resumo),
    habilidades: texto(l.habilidades),
    informacoes_adicionais: texto(l.informacoes_adicionais),
    experiencias: lista(l.experiencias),
    formacoes: lista(l.formacoes),
    cursos: lista(l.cursos),
    idiomas: lista(l.idiomas),
    atualizado_em: texto(l.atualizado_em),
  };
}

/** Os currículos do aluno, do mais recente para o mais antigo. */
export async function listarCurriculos(): Promise<Curriculo[] | null> {
  const { data, error } = await supabase
    .from("curriculos")
    .select(COLUNAS)
    .order("atualizado_em", { ascending: false });
  if (error) return null;
  return (data ?? []).map((l) => daLinha(l as unknown as Record<string, unknown>));
}

export async function lerCurriculo(id: string): Promise<Curriculo | null> {
  const { data, error } = await supabase.from("curriculos").select(COLUNAS).eq("id", id).limit(1);
  // `.eq('id', …)` aqui é conveniência de rede, não segurança: a RLS já limita
  // o resultado ao dono. No app de origem o equivalente era a única barreira.
  if (error || !data || data.length === 0) return null;
  return daLinha(data[0] as unknown as Record<string, unknown>);
}

/** Cria um currículo já preenchido com o que o hub sabe do aluno. */
export async function criarCurriculo(titulo: string): Promise<Escrita<string>> {
  const { data, error } = await supabase.rpc("curriculo_novo_do_perfil", { p_titulo: titulo });
  if (error) return falha(error.message);
  return { ok: true, res: String(data) };
}

export type CamposDoCurriculo = Partial<
  Pick<
    Curriculo,
    | "titulo"
    | "tema"
    | "nome"
    | "email"
    | "telefone"
    | "linkedin"
    | "localidade"
    | "resumo"
    | "habilidades"
    | "informacoes_adicionais"
    | "experiencias"
    | "formacoes"
    | "cursos"
    | "idiomas"
  >
>;

/**
 * Salva. Só o que vem no objeto é enviado — `null` na RPC significa "não mexer",
 * então um campo ausente aqui não apaga o que está gravado.
 *
 * As quatro listas passam por `paraGravar` antes: item totalmente vazio não vale
 * uma linha, e chave que a tela não conhece faria a RPC recusar a lista inteira.
 * Limpar aqui é o que evita o aluno perder o salvamento por causa de um resíduo
 * de estado da tela.
 */
export async function salvarCurriculo(
  id: string | null,
  campos: CamposDoCurriculo,
): Promise<Escrita<string>> {
  const p: Record<string, unknown> = { p_id: id };
  if (campos.titulo !== undefined) p.p_titulo = campos.titulo;
  if (campos.tema !== undefined) p.p_tema = campos.tema;
  if (campos.nome !== undefined) p.p_nome = campos.nome;
  if (campos.email !== undefined) p.p_email = campos.email;
  if (campos.telefone !== undefined) p.p_telefone = campos.telefone;
  if (campos.linkedin !== undefined) p.p_linkedin = campos.linkedin;
  if (campos.localidade !== undefined) p.p_localidade = campos.localidade;
  if (campos.resumo !== undefined) p.p_resumo = campos.resumo;
  if (campos.habilidades !== undefined) p.p_habilidades = campos.habilidades;
  if (campos.informacoes_adicionais !== undefined) {
    p.p_informacoes_adicionais = campos.informacoes_adicionais;
  }
  if (campos.experiencias !== undefined) {
    p.p_experiencias = paraGravar(campos.experiencias, CHAVES_EXPERIENCIA);
  }
  if (campos.formacoes !== undefined) {
    p.p_formacoes = paraGravar(campos.formacoes, CHAVES_FORMACAO);
  }
  if (campos.cursos !== undefined) p.p_cursos = paraGravar(campos.cursos, CHAVES_CURSO);
  if (campos.idiomas !== undefined) p.p_idiomas = paraGravar(campos.idiomas, CHAVES_IDIOMA);

  const { data, error } = await supabase.rpc("curriculo_salvar", p);
  if (error) return falha(error.message);
  return { ok: true, res: String(data) };
}

export async function renomearCurriculo(id: string, titulo: string): Promise<Escrita<null>> {
  const { error } = await supabase.rpc("curriculo_renomear", { p_id: id, p_titulo: titulo });
  if (error) return falha(error.message);
  return { ok: true, res: null };
}

export async function duplicarCurriculo(id: string): Promise<Escrita<string>> {
  const { data, error } = await supabase.rpc("curriculo_duplicar", { p_id: id });
  if (error) return falha(error.message);
  return { ok: true, res: String(data) };
}

export async function excluirCurriculo(id: string): Promise<Escrita<null>> {
  const { error } = await supabase.rpc("curriculo_excluir", { p_id: id });
  if (error) return falha(error.message);
  return { ok: true, res: null };
}
